import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { WEB_BROWSER_MODEL_TIERS } from "@erp/shared-types";
import { VisibilityPredicateType } from "../../registry/visibility";
import { ServerInfrastructureConfigKey } from "../../registry/visibility";

export const webBrowserStep = defineStepConfig({
  type: "web_browser",
  name: "Browser Use Agent",
  description:
    "Opens a web browser and executes AI-driven instructions. Configure dynamic data inputs, a prompt with {{key}} interpolation, credentials, and a starting URL.",
  category: "AI & Agents",
  visibility: [
    [
      {
        type: VisibilityPredicateType.ServerInfrastructureConfig,
        key: ServerInfrastructureConfigKey.BrowserUse,
      },
    ],
  ],

  inputSchema: z.object({}).passthrough(),

  outputSchema: z.object({
    success: z.boolean(),
    message: z.string().nullable(),
    error: z.string().nullable(),
    iterationsUsed: z.number().nullable(),
    downloads: z
      .array(
        z.object({
          documentId: z.string(),
          fileName: z.string(),
          fileSizeBytes: z.number(),
          mimeType: z.string(),
        }),
      )
      .default([])
      .describe(
        "Files the operator downloaded during the session, each captured as a Document. Populated for Kernel sessions only.",
      ),
    documentIds: z
      .array(z.string())
      .default([])
      .describe(
        "Flat list of downloaded Document IDs. Wire this into send_email's documentIds input to attach the files.",
      ),
  }),

  configSchema: z.object({
    dataInputs: z
      .array(
        z.object({
          key: z
            .string()
            .regex(
              /^[a-z][a-z0-9_]*$/,
              "Key must be lowercase alphanumeric with underscores",
            ),
          label: z.string().optional(),
        }),
      )
      .default([])
      .superRefine((inputs, ctx) => {
        const seen = new Set<string>();
        inputs.forEach((input, index) => {
          if (seen.has(input.key)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Duplicate dataInputs[].key: ${input.key}`,
              path: [index, "key"],
            });
            return;
          }
          seen.add(input.key);
        });
      })
      .describe(
        "Dynamic data input sockets. Each becomes an input the user can wire. Reference values in the prompt with {{key}}.",
      ),
    prompt: z
      .string()
      .min(1)
      .describe(
        "Instruction prompt for the browser operator. Use {{key}} to interpolate data input values.",
      ),
    startUrl: z
      .string()
      .url()
      .optional()
      .describe("URL to navigate to when the browser session starts"),
    credentials: z
      .array(
        z.object({
          credentialId: z.string(),
          label: z.string().default(""),
        }),
      )
      .default([])
      .transform((entries) =>
        entries.flatMap((e) => {
          if (!e.credentialId) return [];
          return [
            {
              ...e,
              credentialId: z.string().uuid().parse(e.credentialId),
            },
          ];
        }),
      )
      .describe("Application credentials available to the browser operator"),
    model: z
      .enum(WEB_BROWSER_MODEL_TIERS)
      .default("sonnet")
      .describe("AI model tier for the browser operator"),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 minutes",
    retry: {
      maximumAttempts: 1,
    },
  },
});
